import { useHandleCancelableApi } from 'common/Hooks/UseHandleCancelableApi'
import { downloadXLSX } from 'common/Utils/downloadUtils/downloadXlsx'
import { roundCurrency } from 'common/Utils/NumberUtilities'
import { getDate } from 'common/Utils/StringUtilities'
import Alert from 'components/Common/Alert'
import Breadcrumbs from 'components/Common/Breadcrumb'
import Loader from 'components/Common/Loader'
import SearchInput from 'components/Common/SearchInput'
import TooltipMessage from 'components/Common/TooltipMessage'
import { Table2 } from 'components/Table/Table2'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import Flatpickr from 'react-flatpickr'
import { withTranslation } from 'react-i18next'
import { MetaTags } from 'react-meta-tags'
import { connect } from 'react-redux'
import { Flag } from 'semantic-ui-react'
import { getBillingList } from '../../store/actions'
import BillingFiltersModal from './BillingFiltersModal'

export const BillingList = ({
	t,
	getBillingList,
	billingList,
	count,
	isLoading,
	error,
}) => {

	const [page, setPage] = useState(1)
	const [pageSize, _setPageSize] = useState(10)
	const [dateRange, setDateRange] = useState(null)
	const [keyword, setKeyword] = useState('')
	const [filters, setFilters] = useState({})

	const breadcrumbElement = (
		<Breadcrumbs
			title={t('management', { ns: 'naming' })}
			breadcrumbItems={[
				{
					item: t('billing', { ns: 'naming' }),
					link: '/billing',
				},
			]}
		/>
	)

	const reload = (_, signal) => {
		getBillingList({ page, pageSize, dateRange, keyword, filters }, signal)
	}

	const setPageSize = (pageSize) => {
		if (pageSize == null) reload()
		_setPageSize(pageSize)
	}

	// Go back to the last existing page when the result count shrinks (in its own effect so that updating the count doesn't reload the list):
	useEffect(() => {
		if (!count) return
		const maxPage = pageSize ? Math.ceil(count / pageSize) : 1
		if (page > maxPage) setPage(maxPage)
	}, [count])

	const { handleApiCall } = useHandleCancelableApi(reload, null, 500, true)

	useEffect(() => {
		handleApiCall()
	}, [page, pageSize, dateRange?.startDate, dateRange?.endDate, keyword, filters])

	const handleOnChangeDates = dateStr => {
		const SEPARATOR = 'to'
		const DEFAULT_INITIAL_DATE_HOUR = '00:00:00'
		const DEFAULT_END_DATE_HOUR = '23:59:59'
		if (!dateStr.includes(SEPARATOR)) setDateRange(null)
		const [startDate, endDate] = dateStr.split(SEPARATOR).map(date => date.trim())
		if (!startDate || !endDate) return
		setDateRange({
			startDate: `${startDate} ${DEFAULT_INITIAL_DATE_HOUR}`,
			endDate: `${endDate} ${DEFAULT_END_DATE_HOUR}`,
		})
	}

	// Headers used in both the Table and in the exported Excel:
	const headers = {
		id: 'ID',
		customer: t('client', { ns: 'naming' }),
		country: t('country', { ns: 'naming' }),
		orderDate: t('orderDate', { ns: 'naming' }),
		paymentMethodId: t('paymentMethod', { ns: 'naming' }),
		amountEuro: t('totalPrice', { ns: 'naming' }),
		logisticsCostEuro: t('logistics', { ns: 'naming' }),
		productionCostEuro: t('production', { ns: 'naming' }),
		marginEuro: t('margin_value', { ns: 'naming' }),
		manager: t('manager', { ns: 'naming' }),
	}

	return (
		<div className="page-content mb-4">

			{/* Title and Breadcrumbs: */}
			<MetaTags>
				<title>Proto&Go! | {t('billing', { ns: 'naming' })}</title>
			</MetaTags>
			{breadcrumbElement}

			{/* Page Content: */}
			<div className="bg-white my-4 p-4 marginFooter">
				<div className="py-2">

					{/* Top Bar (always shown): */}
					<div className="d-flex justify-content-between align-items-center">
						<div className="d-flex align-items-center">

							{/* Search Bar: */}
							<div className="position-relative me-3">
								<SearchInput onChange={setKeyword} value={keyword} />
							</div>

							{/* Filters Button (and modal inside): */}
							<BillingFiltersModal setFilters={setFilters} />

							{/* Date Picker: */}
							<div className="ms-3">
								<Flatpickr
									options={{
										mode: 'range',
										dateFormat: 'Y-m-d',
										wrap: true,
									}}
									onChange={(e, dateStr) => handleOnChangeDates(dateStr)}
								>
									<div className="flatpickr" data-input>
										<input type="text" className="d-none" placeholder="Select Date.." />
										<a className="input-button d-flex align-items-center" title="toggle" data-toggle>
											<i className="bx bx-calendar fs-3 text-secondary"></i>
										</a>
									</div>
								</Flatpickr>
							</div>

							{/* Date range selected in the Date Picker (display): */}
							{dateRange && (
								<div className="d-flex align-items-center ms-3">
									<span className="me-1">{`${getDate(dateRange.startDate)} - ${getDate(dateRange.endDate)}`}</span>
									<div
										onClick={() => setDateRange(null)}
										className="btn btn-outline-danger p-0 m-0 d-flex align-items-center h-auto"
									>
										<i className='m-0 p-0 bx bx-x' />
									</div>
								</div>
							)}
						</div>

						{/* Export Excel button: */}
						<button
							className="btn btn-success"
							style={{ width: 'auto' }}
							onClick={() => {
								downloadXLSX({
									rows: [
										// Headers:
										headers,

										// Items:
										...billingList.map(billingItem => ({
											id: billingItem.id,
											customer: billingItem.customer,
											country: t(billingItem.country?.toLowerCase(), { ns: 'countries' }),
											orderDate: getDate(billingItem.orderDate), // TODO formato de fecha no cambia con el idioma
											paymentMethodId: t(`payment_method_${billingItem.paymentMethodId}_name`, { ns: 'paymentMethods' }),
											amountEuro: `${roundCurrency(billingItem.amountEuro)}€`,
											logisticsCostEuro: `${roundCurrency(billingItem.logisticsCostEuro)}€`,
											productionCostEuro: `${roundCurrency(billingItem.productionCostEuro)}€`,
											marginEuro: `${roundCurrency(billingItem.marginEuro)}€`,
											manager: billingItem.managerName,
										})),

										// Totals:
										{
											amountEuro: `${roundCurrency(billingList.reduce((prev, curr) => prev += curr.amountEuro || 0, 0))}€`,
											logisticsCostEuro: `${roundCurrency(billingList.reduce((prev, curr) => prev += curr.logisticsCostEuro || 0, 0))}€`,
											productionCostEuro: `${roundCurrency(billingList.reduce((prev, curr) => prev += curr.productionCostEuro || 0, 0))}€`,
											marginEuro: `${roundCurrency(billingList.reduce((prev, curr) => prev += curr.marginEuro || 0, 0))}€`,
										},
									],
									fileName: 'billingList',
								})
							}}
							disabled={isLoading || billingList.length === 0}
						>
							{t('export', { ns: 'naming' })}
						</button>

					</div>

					{/* Table (hidden when loading/error): */}
					{isLoading ? (

						// Loading Spinner:
						<Loader className="font-size-80 mx-auto" />
					) : error ? (

						// Error Message:
						<Alert type={'danger'} message={error} fitContent centered />
					) : (

						// Table of valid Orders with valid Productions (Billing - Facturación):
						<Table2
							t={t}

							// Headers:
							headers={headers}

							// Items:
							rows={billingList.map(billingItem => ({
								id: billingItem.id,
								customer: billingItem.customer,
								country: (
									<span>
										<TooltipMessage message={t(billingItem.country?.toLowerCase(), { ns: 'countries' })}>
											<Flag id="flagTooltip" name={billingItem.country?.toLowerCase()} />
										</TooltipMessage>
									</span>
								),
								orderDate: getDate(billingItem.orderDate), // TODO formato de fecha no cambia con el idioma
								paymentMethodId: t(`payment_method_${billingItem.paymentMethodId}_name`, { ns: 'paymentMethods' }),
								amountEuro: `${roundCurrency(billingItem.amountEuro)}€`,
								logisticsCostEuro: `${roundCurrency(billingItem.logisticsCostEuro)}€`,
								productionCostEuro: `${roundCurrency(billingItem.productionCostEuro)}€`,
								marginEuro: `${roundCurrency(billingItem.marginEuro)}€`,
								manager: billingItem.managerName,
							}))}

							// Pagination:
							currentPage={page}
							currentPageSize={pageSize}
							setPage={setPage}
							setPageSize={setPageSize}
							totalCount={count}
						/>
					)}
				</div>
			</div>
		</div>
	)
}

BillingList.propTypes = {
	t: PropTypes.func,
	getBillingList: PropTypes.func,
	billingList: PropTypes.array,
	count: PropTypes.number,
	isLoading: PropTypes.bool,
	error: PropTypes.string,
}

const mapStateToProps = state => {
	return {
		billingList: state.Orders.billingList,
		count: state.Orders.count,
		isLoading: state.Orders.isLoadingBillingList,
		error: state.Orders.error,
	}
}

export default connect(mapStateToProps, {
	getBillingList,
})(withTranslation()(BillingList))
